import { useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { ActionButton } from './ActionButton.js'
import { notify } from './Toasts.js'
import { OrtsFeldPaar, StrassenFeld, useOrtschaften, useStrassen } from './OrtsFelder.js'
import { createCatalogItem, createCustomer, createProject } from '../net/records.js'
import { Handelsregister } from './Handelsregister.js'

/**
 * Anlegen, ohne den Beleg zu verlassen.
 *
 * Der Grund ist ein beobachteter Arbeitsablauf und keine Bequemlichkeit: Wer
 * eine Rechnung schreibt und merkt, dass der Kunde noch nicht erfasst ist,
 * musste bisher den halbfertigen Beleg stehen lassen, in die Kundenliste
 * wechseln, anlegen, zurückkommen – und fand einen Bildschirm vor, der
 * inzwischen zurückgesetzt war. Das Ergebnis kennt jeder Betrieb: der Kunde
 * wird nicht angelegt, sondern der Name in ein Freitextfeld getippt, und
 * beim nächsten Mal noch einmal, anders geschrieben.
 *
 * Die drei Masken hier sind bewusst kürzer als die vollen Stammdatenmasken.
 * Sie fragen, was für einen Beleg gebraucht wird. Alles Weitere – Notizen,
 * zweite Adresse, Zuständigkeiten – steht auf dem Stammblatt und kann später
 * dort nachgetragen werden. Eine Schnellanlage, die alles fragt, ist keine.
 *
 * Gespeichert wird sofort und richtig: über dieselben Wege wie die
 * Stammdatenmasken, also in den Ausgangskorb und damit auch im Funkloch. Es
 * entsteht kein Zwischending, das erst beim Abschliessen des Belegs zu einem
 * Datensatz wird – ein halber Kunde, der an einem Beleg hängt, wäre genau die
 * Art Datenleiche, die später niemand erklären kann.
 */

function Kasten({
  titel,
  hinweis,
  kinder,
  aufAbbruch,
  speichern,
  knopfId
}: {
  titel: string
  hinweis?: string
  kinder: JSX.Element
  aufAbbruch: () => void
  speichern: () => Promise<void>
  knopfId: string
}): JSX.Element {
  return (
    <div className="o-anlegen">
      <div className="o-anlegen-kopf">
        <strong>{titel}</strong>
        <button type="button" className="o-btn-ghost" onClick={aufAbbruch}>
          Abbrechen
        </button>
      </div>
      {hinweis && <p className="o-soft o-anlegen-hinweis">{hinweis}</p>}
      {kinder}
      <div className="o-anlegen-fuss">
        <ActionButton
          id={knopfId}
          labels={{ idle: 'Anlegen und übernehmen', busy: 'Legt an …', success: 'Angelegt' }}
          onAction={speichern}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kunde
// ---------------------------------------------------------------------------

export function KundeAnlegen({
  session,
  vorname,
  aufFertig,
  aufAbbruch,
  aufEinstellungen
}: {
  session: SessionUser
  /** Was im Suchfeld stand, als jemand «Neu» gedrückt hat. */
  vorname?: string
  aufFertig: (id: string) => void
  aufAbbruch: () => void
  /** Führt zur Einstellung, falls die Handelsregister-Suche noch fehlt. */
  aufEinstellungen?: () => void
}): JSX.Element {
  interface FelderIntern {
    name: string
    contact: string
    street: string
    zip: string
    city: string
    email: string
    phone: string
    uid: string
  }
  const [f, setF] = useState<FelderIntern>({
    name: vorname ?? '',
    contact: '',
    street: '',
    zip: '',
    city: '',
    email: '',
    phone: '',
    uid: ''
  })
  const ortschaften = useOrtschaften()
  const strassen = useStrassen()
  const setz = (teil: Partial<FelderIntern>): void => setF((alt) => ({ ...alt, ...teil }))

  return (
    <Kasten
      titel="Neuer Kunde"
      hinweis="Name und Adresse genügen. Alles Weitere steht auf dem Kundenblatt."
      knopfId="btn-kunde-schnell"
      aufAbbruch={aufAbbruch}
      speichern={async () => {
        if (!f.name.trim()) {
          notify.warn('Ohne Namen geht es nicht.')
          return
        }
        const id = await createCustomer(
          { tenantId: session.tenantId, userId: session.userId },
          {
            name: f.name.trim(),
            contact: f.contact.trim() || null,
            street: f.street.trim() || null,
            zip: f.zip.trim() || null,
            city: f.city.trim() || null,
            email: f.email.trim() || null,
            phone: f.phone.trim() || null,
            /*
              Die UID kommt aus der Zefix-Suche und hat im Kundenstamm kein
              eigenes Feld. Sie in die Notizen zu schreiben ist ehrlicher, als
              sie wegzuwerfen: sie steht dort, wo ein Mensch sie sucht, und
              behauptet nicht, ein geprüftes Feld zu sein.
            */
            notes: f.uid ? `UID ${f.uid}` : null
          }
        )
        notify.ok(`${f.name.trim()} ist angelegt.`)
        aufFertig(id)
      }}
      kinder={
        <>
          <Handelsregister
            aufUebernehmen={(werte) => setz(werte)}
            aufEinstellungen={aufEinstellungen}
          />
          <div className="o-grid o-grid-2">
            <div className="o-field">
              <label htmlFor="kn-name">Name oder Firma</label>
              <input
                autoComplete="off"
                id="kn-name"
                value={f.name}
                onChange={(e) => setz({ name: e.target.value })}
              />
            </div>
            <div className="o-field">
              <label htmlFor="kn-contact">Ansprechperson</label>
              <input
                autoComplete="off"
                id="kn-contact"
                value={f.contact}
                onChange={(e) => setz({ contact: e.target.value })}
              />
            </div>
          </div>
          <div className="o-grid o-grid-3" style={{ marginTop: 12 }}>
            <div className="o-field">
              <label htmlFor="kn-street">Strasse und Nummer</label>
              <StrassenFeld
                id="kn-street"
                wert={f.street}
                aufWert={(w) => setz({ street: w })}
                aufAdresse={(a) =>
                  setz({ street: a.strasse, zip: a.plz || f.zip, city: a.ort || f.city })
                }
                bestand={strassen}
              />
            </div>
            <OrtsFeldPaar
              praefix="kn"
              ids={{ plz: 'kn-zip', ort: 'kn-city' }}
              plz={f.zip}
              ort={f.city}
              aufAenderung={({ plz, ort }) => setz({ zip: plz, city: ort })}
              ortschaften={ortschaften}
              klassen={{ reihe: null, feld: 'o-field' }}
              hinweis
            />
          </div>
          <div className="o-grid o-grid-2" style={{ marginTop: 12 }}>
            <div className="o-field">
              <label htmlFor="kn-email">E-Mail</label>
              <input
                autoComplete="off"
                id="kn-email"
                type="email"
                inputMode="email"
                value={f.email}
                onChange={(e) => setz({ email: e.target.value })}
              />
            </div>
            <div className="o-field">
              <label htmlFor="kn-phone">Telefon</label>
              <input
                autoComplete="off"
                id="kn-phone"
                inputMode="tel"
                value={f.phone}
                onChange={(e) => setz({ phone: e.target.value })}
              />
            </div>
          </div>
        </>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Projekt
// ---------------------------------------------------------------------------

export function ProjektAnlegen({
  session,
  kundeId,
  vorname,
  adresse,
  aufFertig,
  aufAbbruch
}: {
  session: SessionUser
  kundeId: string
  vorname?: string
  adresse?: { street?: string; zip?: string; city?: string }
  aufFertig: (id: string, name: string) => void
  aufAbbruch: () => void
}): JSX.Element {
  const [name, setName] = useState(vorname ?? '')
  const [street, setStreet] = useState(adresse?.street ?? '')
  const [zip, setZip] = useState(adresse?.zip ?? '')
  const [city, setCity] = useState(adresse?.city ?? '')
  const ortschaften = useOrtschaften()
  const strassen = useStrassen()

  return (
    <Kasten
      titel="Neue Baustelle"
      hinweis="Die Adresse ist mit der Objektadresse vom Beleg vorbelegt."
      knopfId="btn-projekt-schnell"
      aufAbbruch={aufAbbruch}
      speichern={async () => {
        if (!name.trim()) {
          notify.warn('Ohne Bezeichnung geht es nicht.')
          return
        }
        if (!kundeId) {
          notify.warn('Zuerst den Kunden wählen – eine Baustelle gehört genau einem Kunden.')
          return
        }
        const id = await createProject(
          { tenantId: session.tenantId, userId: session.userId },
          {
            customerId: kundeId,
            name: name.trim(),
            street: street.trim() || null,
            zip: zip.trim() || null,
            city: city.trim() || null,
            status: 'offen'
          }
        )
        notify.ok(`${name.trim()} ist angelegt.`)
        aufFertig(id, name.trim())
      }}
      kinder={
        <>
          <div className="o-field">
            <label htmlFor="pn-name">Bezeichnung</label>
            <input
              autoComplete="off"
              id="pn-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Wohnung 3. OG"
            />
          </div>
          <div className="o-grid o-grid-3" style={{ marginTop: 12 }}>
            <div className="o-field">
              <label htmlFor="pn-street">Strasse und Nummer</label>
              <StrassenFeld
                id="pn-street"
                wert={street}
                aufWert={setStreet}
                aufAdresse={(a) => {
                  setStreet(a.strasse)
                  if (a.plz) setZip(a.plz)
                  if (a.ort) setCity(a.ort)
                }}
                bestand={strassen}
              />
            </div>
            <OrtsFeldPaar
              praefix="pn"
              ids={{ plz: 'pn-zip', ort: 'pn-city' }}
              plz={zip}
              ort={city}
              aufAenderung={({ plz, ort }) => {
                setZip(plz)
                setCity(ort)
              }}
              ortschaften={ortschaften}
              klassen={{ reihe: null, feld: 'o-field' }}
            />
          </div>
        </>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Leistung
// ---------------------------------------------------------------------------

export interface NeueLeistung {
  id: string
  code: string
  name: string
  unit: string
  unitPriceRp: number
  vatRate: number
  kind: 'arbeit' | 'material'
}

export function LeistungAnlegen({
  session,
  vorname,
  mwstVorgabe,
  vorhandeneCodes,
  aufFertig,
  aufAbbruch
}: {
  session: SessionUser
  vorname?: string
  mwstVorgabe: number
  /** Damit ein Code nicht zweimal vergeben wird – der Katalog lebt vom Code. */
  vorhandeneCodes: ReadonlyArray<string>
  aufFertig: (eintrag: NeueLeistung) => void
  aufAbbruch: () => void
}): JSX.Element {
  const [code, setCode] = useState('')
  const [name, setName] = useState(vorname ?? '')
  const [unit, setUnit] = useState('Std.')
  const [preis, setPreis] = useState('')
  const [kind, setKind] = useState<'arbeit' | 'material'>('arbeit')

  return (
    <Kasten
      titel="Neue Leistung im Katalog"
      hinweis="Sie steht danach im Katalog und lässt sich beim nächsten Beleg wiederverwenden."
      knopfId="btn-leistung-schnell"
      aufAbbruch={aufAbbruch}
      speichern={async () => {
        const c = code.trim().toUpperCase()
        if (!c || !name.trim()) {
          notify.warn('Code und Bezeichnung braucht es beide.')
          return
        }
        if (vorhandeneCodes.includes(c)) {
          notify.warn(`Den Code ${c} gibt es schon. Der Katalog lebt davon, dass er eindeutig ist.`)
          return
        }
        const rappen = Math.round(Number(preis.replace(',', '.')) * 100)
        if (!Number.isFinite(rappen) || rappen < 0) {
          notify.warn('Der Preis ist keine Zahl.')
          return
        }
        // Der Satz des Betriebs, nicht einer je Leistung – siehe OfficeCatalog.
        const id = await createCatalogItem(
          { tenantId: session.tenantId, userId: session.userId },
          {
            code: c,
            name: name.trim(),
            unit: unit.trim() || 'Stk.',
            unitPriceRp: rappen,
            vatRate: mwstVorgabe,
            kind,
            active: true
          }
        )
        notify.ok(`${c} ist im Katalog.`)
        aufFertig({
          id,
          code: c,
          name: name.trim(),
          unit: unit.trim() || 'Stk.',
          unitPriceRp: rappen,
          vatRate: mwstVorgabe,
          kind
        })
      }}
      kinder={
        <>
          <div className="o-grid o-grid-2">
            <div className="o-field">
              <label htmlFor="ln-code">Code</label>
              <input
                id="ln-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="z.B. SCHL-01"
                autoComplete="off"
              />
            </div>
            <div className="o-field">
              <label htmlFor="ln-name">Bezeichnung</label>
              <input
                autoComplete="off"
                id="ln-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
          <div className="o-grid o-grid-4" style={{ marginTop: 12 }}>
            <div className="o-field">
              <label htmlFor="ln-unit">Einheit</label>
              <input
                autoComplete="off"
                id="ln-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            </div>
            <div className="o-field">
              <label htmlFor="ln-preis">Einzelpreis</label>
              <input
                autoComplete="off"
                id="ln-preis"
                inputMode="decimal"
                value={preis}
                onChange={(e) => setPreis(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="o-field">
              <label htmlFor="ln-kind">Art</label>
              <select
                id="ln-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as 'arbeit' | 'material')}
              >
                <option value="arbeit">Arbeit</option>
                <option value="material">Material</option>
              </select>
            </div>
          </div>
        </>
      }
    />
  )
}
